import * as React from 'react';
import * as t from 'io-ts';
import { SemibigHeader } from '@narvar/nth-bit-header';
import Paragraph from '@narvar/nth-bit-paragraph';
import Divider from '@narvar/nth-bit-divider';
import TextLink from '@narvar/nth-bit-text-link';
import SpinnerIcon from '@narvar/nth-bit-icon-spinner';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import {
  useTranslations,
  withTranslationsProvider,
} from '@narvar/nth-hook-translations';
import Button, { ButtonType } from '@narvar/nth-block-button';
import { usePageContext } from '@narvar/nth-hook-context-page';
import { Column } from '@narvar/nth-grid';
import {
  UI_STATE,
  useRedirectToHoldContext,
} from '../../../hooks/useRedirectToHoldState';
import {
  CarrierKindV,
  LocationDetailV,
  ResponseRedirectToHoldSubmit,
  RequestRedirectToHoldSubmit,
} from '../../../../server/api/schema';
import { submitHold } from '../../../api/redirect-to-hold';
import { decode } from '../../../../server/lib/codec';
import PanelHeaderText from '../../PanelHeaderText';
import {
  ActionsRow,
  ChangeLocationWrapper,
  FedexAttributionWrapper,
  LocationWrapper,
  LogoWrapper,
} from './components';
import FedexAttribution from '../../FedexAttribution';
import ErrorAlert from '../../ErrorAlert';
import LocationAddress from '../LocationAddress';
import LocationLogo from '../LocationLogo';
import CustomComponentContext from '../../CustomComponentContext';

const SubmitInputsV = t.strict({
  carrier: CarrierKindV,
  trackingNumber: t.string,
  selectedLocation: LocationDetailV,
  retailerMoniker: t.string,
  uniqueIdentifier: t.string,
});

interface Props {
  featureName: string;
}

const ConfirmLocationPanel: React.FunctionComponent<Props> = ({
  featureName,
}: Props) => {
  const { track } = React.useContext(CustomComponentContext);
  const { state: rthState, update: updateRthState } =
    useRedirectToHoldContext();
  const { state: pageContext } = usePageContext();

  const {
    redirectToHoldConfirmPickup,
    redirectToHoldChangeLocation,
    redirectToHoldStep3,
    redirectToHoldConfirmLocationHeader,
    redirectToHoldConfirmLocationDescription,
    redirectToHoldSystemError,
    redirectToHoldErrorMissingLocation,
    redirectToHoldSelectLocation,
    redirectToHoldErrorMissingAddress,
    redirectToHoldVerify,
  } = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isErrored, setIsErrored] = React.useState(false);

  const { linksNoneDefault, linksNoneHover } = useTheme();

  const { address, selectedLocation: location } = rthState;

  // For this component to be valid, a selected location must exist
  // If not, then show an error UI to go back and choose a location
  if (location === undefined)
    return (
      <>
        <ErrorAlert
          show
          focusOnShow
          error
          dismissible={false}
          role="alert"
          timeout={0}
          s={{
            padding: '1rem',
            margin: '1rem 0',
            borderRadius: '.25rem',
          }}
        >
          <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
            {redirectToHoldErrorMissingLocation}
          </Paragraph>
        </ErrorAlert>
        <Button
          onClick={() =>
            updateRthState({ ...rthState, uiState: UI_STATE.SELECT_LOCATION })
          }
        >
          {redirectToHoldSelectLocation}
        </Button>
      </>
    );

  // For this component to be valid, a given address must exist
  // If not, then show an error UI to go back and verify an address
  if (address === undefined)
    return (
      <>
        <ErrorAlert
          show
          focusOnShow
          error
          dismissible={false}
          role="alert"
          timeout={0}
          s={{
            padding: '1rem',
            margin: '1rem 0',
            borderRadius: '.25rem',
          }}
        >
          <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
            {redirectToHoldErrorMissingAddress}
          </Paragraph>
        </ErrorAlert>
        <Button
          onClick={() =>
            updateRthState({ ...rthState, uiState: UI_STATE.VERIFY_ADDRESS })
          }
        >
          {redirectToHoldVerify}
        </Button>
      </>
    );

  const logRedirectToHoldEvent = (opts: { clickSubType: string }) => {
    track({
      eventType: 'click',
      ...opts,
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setIsErrored(false);

    logRedirectToHoldEvent({
      clickSubType: 'will_confirm_redirect',
    });

    try {
      const {
        carrier,
        trackingNumber,
        selectedLocation,
        retailerMoniker,
        uniqueIdentifier,
      } = decode(SubmitInputsV, {
        carrier: pageContext.carrier,
        trackingNumber: pageContext.trackingNumbers?.[0],
        selectedLocation: rthState.selectedLocation,
        retailerMoniker: pageContext.retailerMoniker,
        uniqueIdentifier: rthState.uniqueIdentifier,
      });

      const { address: selectedAddress } = selectedLocation;
      const selectedLocationSubmit = {
        street: selectedAddress.street1
          ? [selectedAddress.street1, selectedAddress.street2 || ''].filter(
              (street) => typeof street === 'string' && street,
            )
          : [''],
        city: selectedAddress.city,
        stateOrProvince: selectedAddress.stateOrProvince,
        postalCode: selectedAddress.postalCode,
        countryCode: selectedAddress.countryCode,
      };

      const request: RequestRedirectToHoldSubmit = {
        uniqueIdentifier,
        locationId: selectedLocation.id,
        carrierMoniker: carrier,
        retailerMoniker,
        locationAddress: selectedLocationSubmit,
        addressVerification: address,
      };

      const result: ResponseRedirectToHoldSubmit = await submitHold({
        carrier,
        trackingNumber,
        request,
      });

      setIsSubmitting(false);

      if (!result.success) {
        setIsErrored(true);
        return;
      }

      logRedirectToHoldEvent({
        clickSubType: 'did_confirm_redirect',
      });

      updateRthState({
        ...rthState,
        uiState: UI_STATE.SUCCESS,
      });
    } catch (e) {
      setIsSubmitting(false);
      setIsErrored(true);
    }
  };

  const handleChangeLocation = () => {
    logRedirectToHoldEvent({
      clickSubType: 'change_location',
    });

    updateRthState({
      ...rthState,
      uiState: UI_STATE.SELECT_LOCATION,
    });
  };

  const addressLine1 = `${location.address.street1}${
    location.address.street2 ? ', ' : ''
  }${location.address.street2 || ''}`;
  const addressLine2 = `${location.address.city}, ${location.address.stateOrProvince} ${location.address.postalCode}`;

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      <PanelHeaderText>{redirectToHoldStep3}</PanelHeaderText>
      <SemibigHeader as="h2">
        {redirectToHoldConfirmLocationHeader}
      </SemibigHeader>
      <Paragraph containerCustomStyle={{ margin: '1rem 0 1.5rem 0' }}>
        {redirectToHoldConfirmLocationDescription}
      </Paragraph>
      <Divider />
      <LocationWrapper>
        <LogoWrapper>
          <LocationLogo
            s={{ width: '100%' }}
            providerId={location.providerId}
          />
        </LogoWrapper>
        <LocationAddress
          containerStyle={{ marginLeft: '1rem' }}
          name={location.name || ''}
          addressLine1={addressLine1}
          addressLine2={addressLine2}
          featureName={featureName}
          locationType={location.additionalDescription || ''}
        />
      </LocationWrapper>
      <ErrorAlert
        show={isErrored}
        focusOnShow
        error
        dismissible={false}
        role="alert"
        timeout={0}
        s={{
          padding: '1rem',
          margin: '0 0 2rem 0',
          borderRadius: '.25rem',
        }}
      >
        <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
          {redirectToHoldSystemError}
        </Paragraph>
      </ErrorAlert>
      <ActionsRow>
        <Column xs-columns={12} sm-columns={4}>
          <Button
            big
            type={ButtonType.submit}
            disabled={isSubmitting}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit}
          >
            {isSubmitting ? <SpinnerIcon /> : redirectToHoldConfirmPickup}
          </Button>
        </Column>
        <ChangeLocationWrapper xs-columns={12} sm-columns={4}>
          <TextLink
            linkStyle={{
              sDefault: linksNoneDefault,
              sHover: linksNoneHover,
            }}
            onClick={handleChangeLocation}
          >
            {redirectToHoldChangeLocation}
          </TextLink>
        </ChangeLocationWrapper>
        <FedexAttributionWrapper xs-columns={12} sm-columns={4}>
          <FedexAttribution featureName="redirectToHold" />
        </FedexAttributionWrapper>
      </ActionsRow>
    </div>
  );
};

export default withTranslationsProvider(
  withThemeProvider(ConfirmLocationPanel, {
    themeTokens: ['linksNoneDefault', 'linksNoneHover'],
  }),
  {
    translationKeys: [
      'redirectToHoldConfirmPickup',
      'redirectToHoldChangeLocation',
      'redirectToHoldStep3',
      'redirectToHoldConfirmLocationHeader',
      'redirectToHoldConfirmLocationDescription',
      'redirectToHoldSystemError',
      'redirectToHoldErrorMissingLocation',
      'redirectToHoldSelectLocation',
      'redirectToHoldErrorMissingAddress',
      'redirectToHoldVerify',
    ],
  },
);
